import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ImSpinner3 } from 'react-icons/im';
import { FaSearch, FaChevronDown, FaChevronRight, FaTimesCircle, FaInfoCircle, FaCloud } from 'react-icons/fa';
import User from '../../models/User';

const SELECTED_GREEN = '#00B233';

// Admins section = any role whose name contains "admin" (superadmin, admin,
// limitedadmin, subdomainadmin, ...). Everyone else lands under Technicians.
const isAdminUser = (u) => {
    return (u.roles || []).some(role => (role.name || '').toLowerCase().includes('admin'));
};

const isBlank = (v) => v === undefined || v === null || `${v}`.trim().length === 0;

const hasEmail = (u) => !isBlank(u.email) && u.email.trim().length > 0;

const displayName = (u) => {
    return !isBlank(u.full_name) ? u.full_name.trim() : (u.email || '').trim();
};

// Bold row title: the email (the field that matters for a recipient). Falls
// back to the name, then to a clear "no email" label.
const rowTitle = (u) => {
    if (hasEmail(u)) return u.email.trim();
    if (!isBlank(u.full_name)) return u.full_name.trim();
    return 'No email on file';
};

const roleLabel = (u) => {
    const names = (u.roles || []).map(r => r.title || r.name).filter(Boolean);
    if (names.length > 0) return names.join(', ');
    return isAdminUser(u) ? 'Admin' : 'Technician';
};

const initialsFor = (u) => {
    const name = (u.full_name || '').trim();
    if (name.length > 0) {
        const parts = name.split(/\s+/);
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
        return `${first}${last}`.toUpperCase();
    }
    const email = (u.email || '').trim();
    return email.length > 0 ? email.charAt(0).toUpperCase() : '?';
};

// Deterministic, muted avatar palette - same user always gets the same color.
const AVATAR_COLORS = ['#4a7fb5', '#5b8c5a', '#8a6db1', '#c2784a', '#5a8c8c', '#b15b6d', '#7d7da8'];
const avatarColorFor = (u) => {
    const key = u.uuid || u.email || '';
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash + key.charCodeAt(i)) % AVATAR_COLORS.length;
    }
    return AVATAR_COLORS[hash];
};

const matchesSearch = (u, query) => {
    if (isBlank(query)) return true;
    const q = query.trim().toLowerCase();
    return [u.full_name, u.email, u.title, u.phone]
        .some(field => !isBlank(field) && `${field}`.toLowerCase().includes(q));
};

const InitialsAvatar = ({ user, size = 34 }) => (
    <div style={{
        width: size, height: size, borderRadius: size / 2,
        backgroundColor: hasEmail(user) ? avatarColorFor(user) : '#c7c7cc',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
        <span style={{ color: '#fff', fontSize: size * 0.36, fontWeight: 700 }}>{initialsFor(user)}</span>
    </div>
);

const ReportRecipientsModal = (props) => {
    const { show, onHide, recipients, setRecipients, currentUserEmail, companyId } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    // Sections start collapsed so the user isn't met with a long flat list.
    const [adminsOpen, setAdminsOpen] = useState(false);
    const [techsOpen, setTechsOpen] = useState(false);
    // The user whose details are shown in the info card (null = card closed).
    const [infoUser, setInfoUser] = useState(null);
    // Selection lives in a map keyed by user uuid for O(1) toggling.
    const [selected, setSelected] = useState({});

    // (Re)load the company users and seed the selection each time the modal opens.
    useEffect(() => {
        if (!show) return;

        const seed = {};
        (recipients || []).forEach(r => { if (r && r.uuid) seed[r.uuid] = r; });
        setSelected(seed);
        setSearch('');
        setInfoUser(null);
        setLoadError(null);
        setIsLoading(true);

        let cancelled = false;
        const fetchCompanyUsers = async () => {
            if (isBlank(companyId)) {
                setLoadError('Could not determine your company. Please try again.');
                setIsLoading(false);
                return;
            }
            const filters = [
                { field: 'company_id', value: `${companyId}`, comparison: '=' },
                { field: 'archived', value: 'false', comparison: '=' },
            ];
            try {
                const results = await User.filterBy(filters, 'full_name', 'asc');
                if (cancelled) return;
                const loaded = (results && results.data) || [];
                const adminCount = loaded.filter(isAdminUser).length;
                console.log(`[AddRecipients] loaded ${loaded.length} users: ${adminCount} admins, ${loaded.length - adminCount} technicians`);
                setUsers(loaded);
            } catch (e) {
                if (cancelled) return;
                console.log('[AddRecipients] failed to load company users', e);
                setLoadError("Could not load your company's users. Please try again.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchCompanyUsers();
        return () => { cancelled = true; };
    }, [show, companyId]);

    const toggleUser = (u, value) => {
        setSelected(prev => {
            const next = { ...prev };
            if (value) {
                next[u.uuid] = { uuid: u.uuid, full_name: u.full_name, email: u.email };
            } else {
                delete next[u.uuid];
            }
            return next;
        });
    };

    const clearSelection = () => setSelected({});

    const saveChanges = () => {
        const chosen = Object.values(selected);
        console.log(`[AddRecipients] saving ${chosen.length} recipients:`, chosen.map(r => r.email).join('; '));
        setRecipients(chosen);
        onHide();
    };

    const searching = !isBlank(search);
    const visibleUsers = users.filter(u => matchesSearch(u, search));
    const admins = visibleUsers.filter(isAdminUser);
    const technicians = visibleUsers.filter(u => !isAdminUser(u));
    const selectedCount = Object.keys(selected).length;
    // Per-section counts come from the FULL list (not the search-filtered one) so
    // a selection is never hidden by an active search or a collapsed section.
    const adminSelectedCount = users.filter(u => isAdminUser(u) && selected[u.uuid]).length;
    const techSelectedCount = users.filter(u => !isAdminUser(u) && selected[u.uuid]).length;
    // An active search auto-expands both sections so matches are never hidden.
    const showAdmins = searching || adminsOpen;
    const showTechs = searching || techsOpen;

    const SectionHeader = ({ title, total, selectedInSection, open, onToggle }) => (
        <div
            onClick={onToggle}
            style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                backgroundColor: '#f7f7f7', padding: '10px 14px', cursor: 'pointer',
                borderTop: '0.5px solid #ddd', borderBottom: '0.5px solid #ddd',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {open ? <FaChevronDown size={11} color="#8e8e93" /> : <FaChevronRight size={11} color="#8e8e93" />}
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6d6d72', letterSpacing: 0.8, marginLeft: 9 }}>
                    {title.toUpperCase()}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {selectedInSection > 0 &&
                    <span style={{ fontSize: 12, color: SELECTED_GREEN, fontWeight: 700, marginRight: 10 }}>
                        {selectedInSection} selected
                    </span>
                }
                <span style={{ fontSize: 12, color: '#8e8e93' }}>{total}</span>
            </div>
        </div>
    );

    const renderUserRow = (u) => {
        const enabled = hasEmail(u);
        const isSelected = !!selected[u.uuid];
        return (
            <div
                key={u.uuid}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 14px', borderBottom: '1px solid #eee',
                    backgroundColor: isSelected ? '#f2f8ff' : '#fff',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <InitialsAvatar user={u} size={34} />
                    <span
                        title={rowTitle(u)}
                        style={{
                            fontSize: 14, fontWeight: 600, marginLeft: 12,
                            color: enabled ? '#1c1c1e' : '#9a9a9e',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}
                    >
                        {rowTitle(u)}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <FaInfoCircle
                        size={17}
                        color="#2f6fb2"
                        style={{ marginRight: 14, cursor: 'pointer' }}
                        onClick={() => setInfoUser(u)}
                    />
                    <input
                        type="checkbox"
                        disabled={!enabled}
                        checked={isSelected}
                        onChange={(e) => toggleUser(u, e.target.checked)}
                        style={{ width: 18, height: 18, cursor: enabled ? 'pointer' : 'not-allowed' }}
                    />
                </div>
            </div>
        );
    };

    const InfoRow = ({ label, value, color }) => {
        if (isBlank(value)) return null;
        return (
            <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10.5, color: '#8e8e93', letterSpacing: 0.6 }}>{label.toUpperCase()}</div>
                <div style={{ fontSize: 14, color: color || '#1c1c1e', marginTop: 1 }}>{value}</div>
            </div>
        );
    };

    // Lightweight info "card" rendered as an overlay inside the modal body so we
    // don't stack a second react-bootstrap Modal on top of this one.
    const InfoCard = () => {
        if (!infoUser) return null;
        return (
            <div
                onClick={() => setInfoUser(null)}
                style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 10,
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ backgroundColor: '#fff', borderRadius: 14, width: '100%', maxWidth: 340, padding: 22 }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <InitialsAvatar user={infoUser} size={60} />
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#1c1c1e', marginTop: 10, textAlign: 'center' }}>
                            {displayName(infoUser) || 'Unknown user'}
                        </div>
                        <div style={{ fontSize: 12.5, color: '#8e8e93', marginTop: 2, textAlign: 'center' }}>
                            {roleLabel(infoUser)}
                        </div>
                    </div>
                    <InfoRow label="Title" value={!isBlank(infoUser.title) ? infoUser.title : null} />
                    <InfoRow label="Phone" value={!isBlank(infoUser.phone) ? infoUser.phone : null} />
                    {hasEmail(infoUser)
                        ? <InfoRow label="Email" value={infoUser.email.trim()} />
                        : <InfoRow label="Email" value="No email on file - can't receive reports" color="#c77700" />
                    }
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                        <Button variant="link" onClick={() => setInfoUser(null)} style={{ fontWeight: 600 }}>Done</Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Add Recipients</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ position: 'relative', padding: 0 }}>
                <div style={{ padding: '10px 14px 4px' }}>
                    <div style={{ fontSize: 13, color: '#6d6d72' }}>Select who else receives this report.</div>
                </div>

                {isLoading &&
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <ImSpinner3 className="icon-spin" style={{ fontSize: 28 }} />
                    </div>
                }

                {!isLoading && loadError &&
                    <div style={{ textAlign: 'center', padding: 28 }}>
                        <FaCloud size={30} color="#c7c7cc" />
                        <div style={{ marginTop: 10, color: '#757575', fontSize: 14 }}>{loadError}</div>
                    </div>
                }

                {!isLoading && !loadError &&
                    <div>
                        {/* Search */}
                        <div style={{ padding: '4px 14px 8px' }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', backgroundColor: '#f1f1f3',
                                borderRadius: 8, padding: '0 10px', height: 36,
                            }}>
                                <FaSearch size={13} color="#8e8e93" />
                                <input
                                    placeholder="Search by name, email, title..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ flex: 1, fontSize: 14, marginLeft: 8, border: 'none', background: 'transparent', outline: 'none' }}
                                />
                                {search.length > 0 &&
                                    <FaTimesCircle size={14} color="#c7c7cc" style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />
                                }
                            </div>
                        </div>

                        {/* Selected summary */}
                        {selectedCount > 0 &&
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '0 15px 8px',
                            }}>
                                <span style={{ fontSize: 12.5, color: SELECTED_GREEN, fontWeight: 700 }}>
                                    {`${selectedCount} recipient${selectedCount === 1 ? '' : 's'} selected`}
                                </span>
                                <span onClick={clearSelection} style={{ fontSize: 12.5, color: '#2f6fb2', fontWeight: 600, cursor: 'pointer' }}>
                                    Clear All
                                </span>
                            </div>
                        }

                        {visibleUsers.length === 0 &&
                            <div style={{ textAlign: 'center', padding: 28, color: '#8e8e93', fontSize: 14 }}>
                                {searching ? `No users match "${search.trim()}"` : 'No users found for your company'}
                            </div>
                        }

                        {/* Scroll region for the two sections */}
                        <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                            {/* Admins */}
                            {(!searching || admins.length > 0) &&
                                <div>
                                    <SectionHeader
                                        title="Admins"
                                        total={admins.length}
                                        selectedInSection={adminSelectedCount}
                                        open={showAdmins}
                                        onToggle={() => setAdminsOpen(o => !o)}
                                    />
                                    {showAdmins && admins.map(renderUserRow)}
                                    {showAdmins && admins.length === 0 &&
                                        <div style={{ padding: '9px 14px', color: '#999', fontSize: 13, borderBottom: '1px solid #eee' }}>No admins</div>
                                    }
                                </div>
                            }

                            {/* Technicians */}
                            {(!searching || technicians.length > 0) &&
                                <div>
                                    <SectionHeader
                                        title="Technicians"
                                        total={technicians.length}
                                        selectedInSection={techSelectedCount}
                                        open={showTechs}
                                        onToggle={() => setTechsOpen(o => !o)}
                                    />
                                    {showTechs && technicians.map(renderUserRow)}
                                    {showTechs && technicians.length === 0 &&
                                        <div style={{ padding: '9px 14px', color: '#999', fontSize: 13, borderBottom: '1px solid #eee' }}>No technicians</div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                }

                <InfoCard />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={saveChanges} disabled={isLoading || !!loadError}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReportRecipientsModal;
